/**
 * Centralized logging utility with production-error reporting.
 *
 * Firebase Crashlytics has no web SDK, so production errors are reported by
 * calling the `logClientError` Cloud Function, which writes to the `errorLog`
 * Firestore collection (admin-viewable, rate-limited server-side).
 *
 * In development we log to the console and skip the remote call.
 */

const isDev = process.env.NODE_ENV !== "production";

// Simple in-memory circuit breaker so a broken logging endpoint doesn't
// flood the network or itself generate more errors.
let consecutiveFailures = 0;
const MAX_FAILURES = 3;
let lastFailureAt = 0;
const COOLDOWN_MS = 5 * 60 * 1000;

function reporterAvailable() {
  if (consecutiveFailures < MAX_FAILURES) return true;
  if (Date.now() - lastFailureAt > COOLDOWN_MS) {
    consecutiveFailures = 0;
    return true;
  }
  return false;
}

function sanitizeArg(arg) {
  if (arg instanceof Error) {
    return { name: arg.name, message: arg.message, stack: arg.stack };
  }
  if (typeof arg === "object" && arg !== null) {
    try {
      return JSON.parse(JSON.stringify(arg));
    } catch {
      return String(arg);
    }
  }
  return arg;
}

// Report via callable function. Imports are done lazily so the logger itself
// doesn't pull Firebase into test bundles that mock it separately.
async function reportToFirebase(level, message, args) {
  if (!reporterAvailable()) return;
  try {
    const [{ getFunctions, httpsCallable }, { app }] = await Promise.all([
      import("firebase/functions"),
      import("../firebase"),
    ]);
    const functions = getFunctions(app);
    const logClientError = httpsCallable(functions, "logClientError");
    await logClientError({
      level,
      message: String(message).slice(0, 500),
      args: args.slice(0, 5).map(sanitizeArg),
      url: typeof window !== "undefined" ? window.location.href : null,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : null,
      timestamp: new Date().toISOString(),
    });
    consecutiveFailures = 0;
  } catch {
    consecutiveFailures += 1;
    lastFailureAt = Date.now();
  }
}

const logger = {
  error(message, ...args) {
    if (isDev) {
       
      console.error(message, ...args);
    } else {
      // fire-and-forget; never throw from logger
      reportToFirebase("error", message, args).catch(() => {});
    }
  },

  warn(message, ...args) {
    if (isDev) {
       
      console.warn(message, ...args);
    }
    // warnings are not reported to avoid noise
  },

  info(message, ...args) {
    if (isDev) {
       
      console.info(message, ...args);
    }
  },
};

export default logger;
